import React from "react";
import { CheckIcon, SparklesIcon } from "@heroicons/react/24/solid";
import { toFixed } from "../helper/helper";

export default function SubscriptionCard({
  pkg,
  isPopular = false,
  selected = false,
  isCurrentPlan = false,
  onSelectPlan,
}) {
  const { title, price, duration, duration_type, description } = pkg || {};
  const numPrice = Number(price) || 0;
  const isYearly = duration >= 300 || String(title).toLowerCase().includes("year");

  // Calculate monthly breakdown for yearly
  const monthlyEquivalent = isYearly ? (numPrice / 12).toFixed(2) : null;

  const features = isYearly
    ? [
        "Unlimited access to ALL Exclusive VIP Content",
        "100% Ad-Free experience across all free content",
        "Over 30% savings compared to monthly plan",
        "Ultra HD Video & Highest Bitrate Audio Streaming",
        "Full access to E-Books & Audiobooks",
        "Priority Customer Support & VIP Community Badge",
      ]
    : [
        "Unlimited access to ALL Exclusive VIP Content",
        "100% Ad-Free experience across all free content",
        "Flexible month-to-month plan — cancel anytime",
        "HD Video & High-Quality Audio Streaming",
        "Full access to E-Books & Audiobooks",
        "Standard Customer Support",
      ];

  return (
    <div
      onClick={() => onSelectPlan && onSelectPlan(pkg)}
      className={`relative group rounded-3xl p-6 sm:p-8 flex flex-col justify-between transition-all duration-300 cursor-pointer overflow-hidden border ${
        isPopular
          ? "bg-gradient-to-b from-zinc-900 via-zinc-900/90 to-zinc-950 border-secondary/60 shadow-2xl shadow-secondary/15 ring-1 ring-secondary/40 lg:scale-[1.02]"
          : selected
          ? "bg-zinc-900 border-secondary/80 shadow-xl"
          : "bg-zinc-900/60 hover:bg-zinc-900 border-zinc-800 hover:border-zinc-700 shadow-xl"
      }`}
    >
      {/* Popular / Best Value Ribbon */}
      {isPopular && (
        <div className="absolute top-0 right-0">
          <div className="bg-gradient-to-l from-secondary to-amber-500 text-white text-[11px] font-extrabold uppercase tracking-widest px-4 py-1.5 rounded-bl-2xl shadow-lg flex items-center gap-1.5">
            <SparklesIcon className="w-3.5 h-3.5" />
            <span>Best Value • Save 30%</span>
          </div>
        </div>
      )}

      {/* Plan Header */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span
            className={`w-2.5 h-2.5 rounded-full ${
              isYearly ? "bg-amber-400" : "bg-secondary"
            }`}
          ></span>
          <span className="text-xs uppercase font-extrabold tracking-widest text-zinc-400">
            {isYearly ? "Annual Subscription" : "Monthly Subscription"}
          </span>
        </div>

        <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight mb-2">
          {title || (isYearly ? "BAB Premium Yearly" : "BAB Premium Monthly")}
        </h3>

        <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed mb-6 line-clamp-2">
          {description ||
            "Get unlimited access to exclusive subscriber content and watch free videos completely ad-free."}
        </p>

        {/* Price display */}
        <div className="mb-6 pb-6 border-b border-zinc-800/80">
          <div className="flex items-baseline gap-1.5">
            <span className="text-4xl sm:text-5xl font-black text-white tracking-tight">
              ${toFixed(numPrice)}
            </span>
            <span className="text-sm font-semibold text-zinc-400">
              / {isYearly ? "year" : "month"}
            </span>
          </div>
          {monthlyEquivalent && (
            <p className="text-xs font-semibold text-emerald-400 mt-1 flex items-center gap-1">
              <span>Just ${monthlyEquivalent}/month, billed annually</span>
            </p>
          )}
        </div>

        {/* Feature List */}
        <div className="space-y-3.5 mb-8">
          <span className="text-xs font-bold uppercase tracking-wider text-zinc-300 block mb-2">
            Included with this plan:
          </span>
          {features.map((feat, idx) => (
            <div key={idx} className="flex items-start gap-3 text-xs sm:text-sm text-zinc-300">
              <div
                className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5 ${
                  isPopular
                    ? "bg-secondary/20 text-secondary border border-secondary/30"
                    : "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                }`}
              >
                <CheckIcon className="w-3.5 h-3.5 stroke-[3]" />
              </div>
              <span className="leading-snug">{feat}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Action CTA */}
      <div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSelectPlan && onSelectPlan(pkg);
          }}
          className={`w-full py-3.5 px-6 rounded-xl font-extrabold text-sm transition-all duration-300 shadow-lg active:scale-95 flex items-center justify-center gap-2 ${
            isCurrentPlan
              ? "bg-zinc-800 text-zinc-400 cursor-default"
              : isPopular
              ? "bg-secondary hover:bg-secondary/90 text-white shadow-secondary/30 hover:scale-[1.02]"
              : "bg-white hover:bg-zinc-100 text-black shadow-white/10 hover:scale-[1.02]"
          }`}
        >
          {isCurrentPlan ? (
            "Current Active Plan"
          ) : (
            <>
              <span>Subscribe to {isYearly ? "Yearly" : "Monthly"}</span>
              <span className="text-xs opacity-75">→</span>
            </>
          )}
        </button>

        <p className="text-center text-[11px] text-zinc-500 mt-3">
          Cancel anytime • Instant access
        </p>
      </div>
    </div>
  );
}
